import React,{useState,useRef,useCallback,useEffect,useMemo} from 'react';
import {AdminApp} from '../apps/AdminApp';
import {PaywallScreen} from '../apps/AuthScreens';
import {TeamChatView} from '../components/Chat';
import {PlayerLeaguesView} from '../components/Leagues';
import {RatingTimeline,ReadOnlyPitch} from '../components/Match';
import {MyPaymentsView} from '../components/Payments';
import {AccessibilitySettingsPanel,AchievementBadges,DevPointsPanel,Flag,HomeAwaySplitCard,MatchCalendarView,MatchKeyInfo,NotesThread,NotificationSettingsPanel,PitchBackdrop,PlayerDevelopmentSection,PlayerPhotoUploader,ReorderableBlocks,StatsFilterMenu,useStatsVisibility} from '../components/Shared';
import {api} from '../lib/api';
import {LOGO_ICON_DATA_URI} from '../lib/assets';
import {PLAYER_HOME_DEFAULT_ORDER} from '../lib/constants';
import {sendPushNotification} from '../lib/pushNotifications';
import {Icon} from '../components/Icons';
import {computeEntitlements,enrich,fmtDate,fmtDateShort,fmtMins,loadBlockOrder,modalBackEffect,modalPopSuppress,nameSimilarity,rb,saveBlockOrder} from '../lib/utils';

export function PlayerApp({auth,onSignOut}){
  const [team,setTeam]=useState(null);
  const [players,setPlayers]=useState([]);
  const [fixtures,setFixtures]=useState([]);
  const [sessions,setSessions]=useState([]);
  const [loading,setLoading]=useState(true);
  // Land straight back on Payments if we're returning from a Stripe Checkout redirect
  // (?payment=success/cancelled in the URL) rather than defaulting to Home — the whole
  // point of coming back is to see the payment confirmation, not to have to find the
  // tab again after a login that (with session persistence) shouldn't even be necessary.
  const [view,setView]=useState(()=>new URLSearchParams(window.location.search).get("payment")?"payments":"home"); // home | matches | players | stats
  // Which Stats page cards are currently visible — defaults to "All" on every page load.
  const [statsVisible,setStatsVisible]=useStatsVisibility([{id:"matchResults"},{id:"homeAway"},{id:"ratingTimeline"},{id:"goalsAssists"},{id:"attendance"},{id:"matchTime"},{id:"topScorers"},{id:"topAssisters"}]);
  // Whether there's actually a payment plan this player is allowed to see for their
  // team — RLS only lets a player SELECT a payment_plans row when the coach has
  // switched "Visible to Players" on for it, so an empty result means either there's
  // no plan yet or it's specifically been kept hidden from players.
  // Tri-state (null = not loaded yet, true/false = known) so the deep-link straight to
  // "payments" above isn't immediately bounced back to Home by the safety-net effect
  // below before this has had a chance to load — null just means "don't know yet".
  const [paymentsVisible,setPaymentsVisible]=useState(null);
  useEffect(()=>{
    if(!team?.id){setPaymentsVisible(false);return;}
    let cancelled=false;
    api.get(auth.token,"payment_plans",`team_id=eq.${team.id}&select=id`).then(rows=>{
      if(!cancelled)setPaymentsVisible(!!(rows&&rows.length>0));
    });
    return()=>{cancelled=true;};
  },[team?.id]);
  // Safety net: if the Payments tab was open and then becomes unavailable (switched
  // team, or the coach turned "Visible to Players" off), bounce back to Home rather
  // than leaving the player stuck on a tab that's no longer in the nav bar. Only fires
  // once paymentsVisible is actually known (false), not while it's still null.
  useEffect(()=>{
    if(view==="payments"&&paymentsVisible===false)setView("home");
  },[view,paymentsVisible]);
  const [viewer,setViewer]=useState(auth.viewer); // local copy so we can reflect link-request changes immediately
  const [roster,setRoster]=useState(null); // team roster for the "which player am I" picker, lazy-loaded
  const [linkPick,setLinkPick]=useState("");
  const [teamStrategies,setTeamStrategies]=useState([]);
  useEffect(()=>{
    if(team?.id)api.get(auth.token,"team_strategies",`team_id=eq.${team.id}&order=created_at.asc`).then(rows=>setTeamStrategies(rows||[]));
  },[team?.id]);

  const [linkStage,setLinkStage]=useState("suggest"); // "suggest" | "list" — moves to full list once the top suggestion is declined
  const [linkSaving,setLinkSaving]=useState(false);
  // Braces matter here. Written as a concise arrow — useEffect(()=>window.scrollTo(0,0))
  // — this effect *returns* whatever window.scrollTo() gives back, and React treats an
  // effect's return value as its clean-up function. window.scrollTo normally returns
  // undefined (which React ignores), but anything that patches or wraps it — a browser
  // extension, a smooth-scroll polyfill, an accessibility tool — can make it return a
  // non-function, and React then throws "destroy is not a function" while tearing the
  // effect down. Because the dependency is [view], that teardown ran on every single
  // nav click and on sign-out, blanking the whole app. Never return the result of an
  // expression from an effect that has no clean-up.
  useEffect(()=>{ window.scrollTo(0,0); },[view]);
  const [detailMatch,setDetailMatch]=useState(null);
  const [matchTab,setMatchTab]=useState("result");
  function openMatchDetail(m){ setDetailMatch(m); setMatchTab("result"); }
  const [matchPlayerNotes,setMatchPlayerNotes]=useState([]);
  useEffect(()=>{
    if(!detailMatch){setMatchPlayerNotes([]);return;}
    api.get(auth.token,"fixture_player_notes",`fixture_id=eq.${detailMatch.id}`).then(rows=>setMatchPlayerNotes(rows||[]));
  },[detailMatch?.id]);
  const [myReview,setMyReview]=useState(null); // {id} if reviewed, null if not (or not yet loaded)
  useEffect(()=>{
    if(!detailMatch||!viewer.player_id){setMyReview(null);return;}
    api.get(auth.token,"match_reviews",`fixture_id=eq.${detailMatch.id}&player_id=eq.${viewer.player_id}`).then(rows=>setMyReview(rows&&rows[0]?rows[0]:null));
  },[detailMatch?.id,viewer.player_id]);
  async function toggleMyReview(){
    if(!detailMatch||!viewer.player_id)return;
    if(myReview){
      await api.del(auth.token,"match_reviews",myReview.id);
      setMyReview(null);
    } else {
      const created=await api.post(auth.token,"match_reviews",[{fixture_id:detailMatch.id,player_id:viewer.player_id}]);
      if(created&&created[0])setMyReview(created[0]);
    }
  }
  const [myRatings,setMyRatings]=useState([]); // every match_ratings row for the linked player (both sources), across all matches
  async function loadMyRatings(){
    if(!viewer.player_id)return;
    const rows=await api.get(auth.token,"match_ratings",`player_id=eq.${viewer.player_id}`);
    setMyRatings(rows||[]);
  }
  useEffect(()=>{ if(viewer.player_id)loadMyRatings(); },[viewer.player_id]);
  const [myMatchTime,setMyMatchTime]=useState([]); // this player's own player_match_time rows only
  async function loadMyMatchTime(){
    if(!viewer.player_id)return;
    const rows=await api.get(auth.token,"player_match_time",`player_id=eq.${viewer.player_id}`);
    setMyMatchTime(rows||[]);
  }
  useEffect(()=>{ if(viewer.player_id)loadMyMatchTime(); },[viewer.player_id]);
  async function setMySelfRating(fixtureId,rating){
    const existing=myRatings.find(r=>r.fixture_id===fixtureId&&r.source==="player");
    if(existing){
      await api.patch(auth.token,"match_ratings",existing.id,{rating,updated_at:new Date().toISOString()});
      setMyRatings(prev=>prev.map(r=>r.id===existing.id?{...r,rating}:r));
    } else {
      const created=await api.post(auth.token,"match_ratings",[{fixture_id:fixtureId,player_id:viewer.player_id,source:"player",rating}]);
      if(created&&created[0])setMyRatings(prev=>[...prev,created[0]]);
    }
  }
  const [myAchievements,setMyAchievements]=useState([]);
  const [teamAchievements,setTeamAchievements]=useState([]);
  useEffect(()=>{
    if(viewer.player_id)api.get(auth.token,"player_achievements",`player_id=eq.${viewer.player_id}`).then(rows=>setMyAchievements(rows||[]));
  },[viewer.player_id]);
  useEffect(()=>{
    if(viewer.team_id)api.get(auth.token,"team_achievements",`team_id=eq.${viewer.team_id}`).then(rows=>setTeamAchievements(rows||[]));
  },[viewer.team_id]);
  const newAchievements=(()=>{
    const seenAt=viewer.achievements_seen_at?new Date(viewer.achievements_seen_at):null;
    const isNew=r=>!seenAt||new Date(r.created_at)>seenAt;
    return [...myAchievements.filter(isNew),...teamAchievements.filter(isNew)];
  })();
  async function dismissNewAchievements(){
    const now=new Date().toISOString();
    const res=await api.patch(auth.token,"team_viewers",viewer.id,{achievements_seen_at:now});
    if(!res||res.length===0)return;
    setViewer(v=>({...v,achievements_seen_at:now}));
  }
  const [expT,setExpT]=useState({});
  const [matchesViewMode,setMatchesViewMode]=useState("list"); // "list" | "calendar"
  const [isMobile,setIsMobile]=useState(()=>window.innerWidth<640);
  useEffect(()=>{const h=()=>setIsMobile(window.innerWidth<640);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h);},[]);
  // Tablet (e.g. iPad) widths keep the desktop layout, but the top tab row gets too
  // cramped to fit — so from this width down, switch just the navigation to the same
  // bottom nav bar mobile uses.
  const [isBottomNav,setIsBottomNav]=useState(()=>window.innerWidth<1024);
  useEffect(()=>{const h=()=>setIsBottomNav(window.innerWidth<1024);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h);},[]);
  const [mode,setMode]=useState(()=>{try{return localStorage.getItem("sm_theme_mode")||"light";}catch(e){return "light";}});
  useEffect(()=>{document.documentElement.setAttribute("data-theme-mode",mode);try{localStorage.setItem("sm_theme_mode",mode);}catch(e){}},[mode]);
  useEffect(()=>modalBackEffect(detailMatch!==null,()=>setDetailMatch(null)),[detailMatch!==null]);
  const [homeOrder,setHomeOrder]=useState(PLAYER_HOME_DEFAULT_ORDER);
  useEffect(()=>{ loadBlockOrder(auth.token,viewer.id,"player_home",PLAYER_HOME_DEFAULT_ORDER).then(setHomeOrder); },[]);

  function navigateView(v){
    window.history.pushState({__nav:true,view:v},"");
    setView(v);
  }
  // Floor guard: the first page the app lands on (usually Home) is as far back as the
  // app should ever go — the back button must never fall through to whatever real
  // browser history existed before the app loaded (which can look like a silent
  // logout). Once resting on the floor, another back press asks before doing anything.
  const floorViewRef=useRef(view);
  const atFloorRef=useRef(false);
  const [showLogoutConfirm,setShowLogoutConfirm]=useState(false);
  // detailMatch already uses modalBackEffect (closes on back). When it's open, a back
  // press belongs to it — skip our own handling so closing it never also triggers a
  // spurious "log out?" prompt if it happens to close back down onto the floor page.
  const modalOpenRef=useRef(false);
  useEffect(()=>{ modalOpenRef.current=detailMatch!==null; },[detailMatch]);
  useEffect(()=>{
    window.history.replaceState({__nav:true,view:floorViewRef.current},"");
    function onPop(e){
      if(modalPopSuppress.active>0){ modalPopSuppress.active--; return; }
      if(modalOpenRef.current)return;
      const s=e.state;
      if(!s||!s.__nav){
        window.history.pushState({__nav:true,view:floorViewRef.current},"");
        setShowLogoutConfirm(true);
        return;
      }
      const v=s.view??"home";
      const atFloorNow=v===floorViewRef.current;
      if(atFloorNow&&atFloorRef.current){
        window.history.pushState({__nav:true,view:floorViewRef.current},"");
        setShowLogoutConfirm(true);
        return;
      }
      atFloorRef.current=atFloorNow;
      setView(v);
      if(atFloorNow)window.history.pushState({__nav:true,view:floorViewRef.current},"");
    }
    window.addEventListener("popstate",onPop);
    return ()=>window.removeEventListener("popstate",onPop);
  },[]);

  const AS={accentColor:"#10b981",bgColor:"var(--c-bg0)",surfaceColor:"var(--c-surface)",borderColor:"var(--c-border)",appName:"pitchMGR"};
  const ac=AS.accentColor;
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};

  useEffect(()=>{ load(); },[]);
  const [allSeasons,setAllSeasons]=useState([]);
  const [viewSeasonId,setViewSeasonId]=useState(null); // null = current/live season
  useEffect(()=>{
    if(auth.viewer.org_id)api.get(auth.token,"seasons",`org_id=eq.${auth.viewer.org_id}&order=created_at.desc`).then(rows=>setAllSeasons(rows||[]));
  },[]);
  const [orgOpenToLeagues,setOrgOpenToLeagues]=useState(false);
  const [orgBilling,setOrgBilling]=useState(null); // {name,trial_ends_at,subscription_active} — for the trial paywall
  useEffect(()=>{
    if(auth.viewer.org_id)api.get(auth.token,"organisations",`id=eq.${auth.viewer.org_id}&select=name,open_to_leagues,trial_ends_at,subscription_active,plan_tier,billing_cycle,parent_addon,player_addon,feature_achievements,feature_player_development,pitch_color_1,pitch_color_2,pitch_brightness,pitch_match_halves,pitch_stripes,feature_player_ratings,feature_leagues,feature_payments,stripe_charges_enabled`).then(rows=>{
      setOrgOpenToLeagues(!!(rows&&rows[0]&&rows[0].open_to_leagues));
      setOrgBilling(rows&&rows[0]||null);
    });
  },[]);
  async function switchSeason(seasonId){
    setViewSeasonId(seasonId);
    if(!seasonId){await load();return;}
    if(!auth.viewer.player_id){setTeam(null);setPlayers([]);setFixtures([]);setSessions([]);return;}
    const tps=await api.get(auth.token,"team_players",`player_id=eq.${auth.viewer.player_id}&select=team_id`);
    const teamIds=(tps||[]).map(t=>t.team_id);
    if(teamIds.length===0){setTeam(null);setPlayers([]);setFixtures([]);setSessions([]);return;}
    const teamsInSeason=await api.get(auth.token,"teams",`id=in.(${teamIds.join(",")})&season_id=eq.${seasonId}&select=id`);
    const foundTeamId=teamsInSeason&&teamsInSeason[0]?teamsInSeason[0].id:null;
    if(!foundTeamId){setTeam(null);setPlayers([]);setFixtures([]);setSessions([]);return;}
    await load(foundTeamId);
  }

  async function load(teamIdOverride){
    setLoading(true);
    try{
      const teamId=teamIdOverride||auth.viewer.team_id;
      if(!teamId){setTeam(null);setPlayers([]);setFixtures([]);setSessions([]);setLoading(false);return;}
      const ts=await api.get(auth.token,"teams",`id=eq.${teamId}`);
      if(ts&&ts[0])setTeam(ts[0]);
      // Players are org-wide; this team's roster + stats live in team_players
      const tps=await api.get(auth.token,"team_players",`team_id=eq.${teamId}`);
      if(tps&&tps.length>0){
        const pids=tps.map(tp=>tp.player_id).join(",");
        // Explicit column list, not the whole row. Hiding a teammate's date of birth
        // in the UI is not the same as it being private — an unrestricted select ships
        // every child's exact DOB to this browser, where it sits in the network tab and
        // in memory for anyone who looks. These six columns are everything the player
        // app renders about a teammate; dob is not among them and must not be added
        // back. A player's own DOB isn't needed here either: nothing in this app shows
        // it to them, and they already know it.
        const basePlayers=await api.get(auth.token,"players",`id=in.(${pids})&select=id,name,number,position,nationality,photo_url`);
        const joined=(basePlayers||[]).map(bp=>{
          const tp=tps.find(t=>t.player_id===bp.id);
          return{...bp,status:tp?.status||"Available",goals:tp?.goals||0,assists:tp?.assists||0};
        }).sort((a,b)=>(a.number||999)-(b.number||999));
        setPlayers(joined);
      } else {
        setPlayers([]);
      }
      const fs2=await api.get(auth.token,"fixtures",`team_id=eq.${teamId}&order=date.desc`);
      if(fs2&&fs2.length>0){
        const fids=fs2.map(f=>f.id).join(",");
        const evts=await api.get(auth.token,"fixture_events",`fixture_id=in.(${fids})`);
        const top=fs2.filter(f=>!f.tournament_id);
        setFixtures(top.map(f=>f.type==="tournament"?{...enrich(f,evts),matches:fs2.filter(m=>m.tournament_id===f.id).map(m=>enrich(m,evts))}:enrich(f,evts)));
      } else { setFixtures([]); }
      const ss=await api.get(auth.token,"sessions",`team_id=eq.${teamId}`);
      if(ss&&ss.length>0){
        const sids=ss.map(s=>s.id).join(",");
        const att=await api.get(auth.token,"attendance",`session_id=in.(${sids})`);
        setSessions(ss.map(s=>({...s,attendance:Object.fromEntries((att||[]).filter(a=>a.session_id===s.id).map(a=>[a.player_id,a.status]))})));
      } else { setSessions([]); }
    } catch(e){ console.error("Player app load error:",e); }
    setLoading(false);
  }

  async function loadRoster(){
    const teamId=auth.viewer.team_id;
    const tps=await api.get(auth.token,"team_players",`team_id=eq.${teamId}&select=player_id`);
    if(!tps||tps.length===0){setRoster([]);return;}
    const ids=tps.map(t=>t.player_id).join(",");
    const ps=await api.get(auth.token,"players",`id=in.(${ids})&select=id,name&order=name.asc`);
    setRoster(ps||[]);
  }
  const [linkError,setLinkError]=useState("");
  async function requestLink(pid){
    const id=pid||linkPick;
    if(!id)return;
    setLinkSaving(true);setLinkError("");
    const res=await api.patch(auth.token,"team_viewers",viewer.id,{player_id:id,player_link_approved:false});
    setLinkSaving(false);
    if(!res||res.length===0){setLinkError("Couldn't save your request — please try again, or ask your coach to check the app is set up correctly.");return;}
    setViewer(v=>({...v,player_id:id,player_link_approved:false}));
    if(team?.org_id){
      const claimedPlayer=(roster||[]).find(p=>p.id===id);
      api.get(auth.token,"org_coaches",`org_id=eq.${team.org_id}&select=coach_id`).then(ocs=>{
        const coachIds=(ocs||[]).map(o=>o.coach_id);
        sendPushNotification(auth.token,coachIds,"new_message","Player Link Request 👋",`${viewer.name} wants to link their account to ${claimedPlayer?.name||"a player"} on your roster — approve it from your dashboard.`);
      });
    }
  }
  async function undoLinkRequest(){
    const res=await api.patch(auth.token,"team_viewers",viewer.id,{player_id:null,player_link_approved:false});
    if(!res||res.length===0){setLinkError("Couldn't undo — please try again.");return;}
    setViewer(v=>({...v,player_id:null,player_link_approved:false}));
    setLinkPick("");setLinkStage("suggest");
  }

  const [playerUnread,setPlayerUnread]=useState(0);
  async function loadPlayerUnread(){
    if(!viewer.player_id||!team)return;
    const ts=await api.get(auth.token,"player_notes",`org_id=eq.${team.org_id}&player_id=eq.${viewer.player_id}&select=id`);
    if(!ts||ts.length===0){setPlayerUnread(0);return;}
    const unread=await api.get(auth.token,"player_note_messages",`thread_id=eq.${ts[0].id}&sender_type=eq.coach&read_by_recipient=eq.false&select=id`);
    setPlayerUnread((unread||[]).length);
  }
  useEffect(()=>{ if(viewer.player_id&&team)loadPlayerUnread(); },[viewer.player_id,team]);
  useEffect(()=>{ const t=setInterval(()=>{ if(viewer.player_id&&team)loadPlayerUnread(); },30000); return()=>clearInterval(t); },[viewer.player_id,team]);

  const stats={
    total:players.length,
    goals:players.reduce((s,p)=>s+(p.goals||0),0),
    assists:players.reduce((s,p)=>s+(p.assists||0),0),
    top:[...players].sort((a,b)=>(b.goals||0)-(a.goals||0))[0],
  };

  const playedMatches=fixtures.filter(f=>!f.matches&&f.played);
  const upcomingMatches=fixtures.filter(f=>!f.matches&&!f.played);
  const tournaments=fixtures.filter(f=>f.matches);

  // Appearances per player, for the MP column on the squad list. There's no stored
  // "matches played" figure, so it's counted from the lineups of matches actually
  // marked as played — a player counts once for a match if they were named in the
  // starting XI or among the substitutes. Tournament fixtures hold their games in
  // .matches rather than being games themselves, so they're flattened first,
  // otherwise every tournament would be missed and its individual games with it.
  const matchesPlayedById=useMemo(()=>{
    const flat=fixtures.flatMap(f=>f.matches?f.matches:[f]);
    const counts={};
    flat.filter(f=>f.played).forEach(f=>{
      const ids=new Set([...(f.slots||[]).map(s=>s.playerId).filter(Boolean),...(f.subs||[])]);
      ids.forEach(id=>{counts[id]=(counts[id]||0)+1;});
    });
    return counts;
  },[fixtures]);

  const linkedPlayer=viewer.player_id?players.find(p=>p.id===viewer.player_id)||null:null;
  const [chatMode,setChatMode]=useState("team"); // "team" | "coach" — on the Chat tab
  function pctOf(b){ return b.total>0?Math.round((b.present/b.total)*100):null; }
  const myAtt=(()=>{
    if(!viewer.player_id)return null;
    const buckets={game:{total:0,present:0},training:{total:0,present:0}};
    sessions.forEach(s=>{
      const status=s.attendance?.[viewer.player_id]||"absent";
      const b=s.type==="game"?buckets.game:buckets.training;
      b.total++;
      if(status==="present"||status==="late")b.present++;
    });
    return{match:pctOf(buckets.game),training:pctOf(buckets.training),overall:pctOf({total:buckets.game.total+buckets.training.total,present:buckets.game.present+buckets.training.present})};
  })();
  const teamAtt=(()=>{
    const buckets={game:{total:0,present:0},training:{total:0,present:0}};
    sessions.forEach(s=>{
      const b=s.type==="game"?buckets.game:buckets.training;
      players.forEach(p=>{
        const status=s.attendance?.[p.id]||"absent";
        b.total++;
        if(status==="present"||status==="late")b.present++;
      });
    });
    return{match:pctOf(buckets.game),training:pctOf(buckets.training),overall:pctOf({total:buckets.game.total+buckets.training.total,present:buckets.game.present+buckets.training.present})};
  })();
  const nextFixture=[...upcomingMatches].sort((a,b)=>new Date(a.date)-new Date(b.date))[0];
  const recentFixtures=[...playedMatches].sort((a,b)=>new Date(b.date)-new Date(a.date)).slice(0,2);
  const avgRating=src=>{const vals=myRatings.filter(r=>r.source===src).map(r=>r.rating);return vals.length>0?Math.round((vals.reduce((a,b)=>a+b,0)/vals.length)*10)/10:null;};
  const avgSelfRating=avgRating("player");
  const avgCoachRating=avgRating("coach");

  useEffect(()=>{ if(!viewer.player_id&&roster===null)loadRoster(); },[viewer.player_id]);

  const featAchievements=orgBilling?.feature_achievements!==false;
  const featPlayerDev=orgBilling?.feature_player_development!==false;
  const featPlayerRatings=orgBilling?.feature_player_ratings!==false;
  const featMatchDay=!!orgBilling?.feature_match_day;
  const featLeagues=orgBilling?.feature_leagues!==false;
  const featPayments=orgBilling?.feature_payments!==false;

  const navTabs=[["home","home","Home"],["matches","calendar","Matches"],["players","users","Players"],["stats","chart","Stats"],...(featLeagues?[["leagues","trophy","Leagues"]]:[]),...(featPayments&&paymentsVisible?[["payments","note","Payments"]]:[]),["chat","chat","Chat"],["notifications","bell","Alerts"],["accessibility","access","Accessibility"]];

  if(loading)return(<div style={{minHeight:"100vh",background:"var(--c-bg0)",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:10}}><div style={{fontSize:32}}>⚽</div><div style={{color:"var(--c-muted)",fontSize:13}}>Loading…</div></div>);

  if(orgBilling&&computeEntitlements(orgBilling).status==="expired")return <PaywallScreen org={orgBilling} isHead={false} onSignOut={onSignOut}/>;

  return(<div style={{minHeight:"100vh",background:mode==="dark"?AS.bgColor:"transparent",fontFamily:"'Inter',system-ui,sans-serif",color:"var(--c-text)",position:"relative"}}>
    {mode!=="dark"&&<PitchBackdrop color1={orgBilling?.pitch_color_1} color2={orgBilling?.pitch_color_2} brightness={orgBilling?.pitch_brightness} matchHalves={orgBilling?.pitch_match_halves} stripes={orgBilling?.pitch_stripes}/>}
    {/* Header */}
    <header style={{position:"sticky",top:0,zIndex:120,background:mode==="dark"?AS.surfaceColor:"rgba(255,255,255,0.94)",backdropFilter:mode==="dark"?undefined:"blur(8px)",borderBottom:`1px solid ${AS.borderColor}`,padding:"0 14px"}}>
      <div style={{maxWidth:isBottomNav?900:1120,margin:"0 auto",display:"flex",alignItems:"center",gap:isMobile?6:9,height:isMobile?50:55}}>
        <img src={LOGO_ICON_DATA_URI} alt="pitchMGR" style={{width:isMobile?22:26,height:isMobile?22:26,borderRadius:6,flexShrink:0}}/>
        {!isMobile&&<span style={{fontWeight:700,fontSize:16,letterSpacing:"-0.3px"}}>pitch<span style={{color:ac}}>MGR</span></span>}
        {team&&<div style={{display:"flex",alignItems:"center",gap:5,padding:"3px 9px",borderRadius:18,background:ac+"22",border:`1px solid ${ac}55`}}>
          <span style={{fontSize:12}}>{team.badge}</span><span style={{fontSize:11,fontWeight:600,color:ac}}>{team.name}</span>
        </div>}
        <div title={viewer.name} style={{display:"flex",alignItems:"center",gap:5,padding:"3px 8px 3px 3px",borderRadius:18,background:AS.bgColor,border:`1px solid ${AS.borderColor}`,overflow:"hidden",minWidth:0,flexShrink:isMobile?1:0}}>
          <div style={{width:18,height:18,borderRadius:"50%",background:ac+"33",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,color:ac,flexShrink:0}}>{(viewer.name||"?").charAt(0).toUpperCase()}</div>
          {!isMobile&&<span style={{fontSize:11,fontWeight:600,color:"var(--c-text2)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{viewer.name}</span>}
        </div>
        {/* On desktop the nav lives in the left sidebar now (see below) instead of
            crowding this top bar — this row just keeps identity/status/actions. */}
        <span style={{marginLeft:"auto",fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:12,background:"#10b98122",color:"#10b981",flexShrink:0}}>PLAYER VIEW</span>
        <button onClick={()=>navigateView("home")} style={{position:"relative",padding:"5px 7px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:13}} title="Notifications">🔔{playerUnread>0&&<span style={{position:"absolute",top:-4,right:-4,fontSize:8,fontWeight:800,background:"#ef4444",color:"#fff",borderRadius:8,padding:"1px 4px",minWidth:14,textAlign:"center"}}>{playerUnread}</span>}</button>
        <button onClick={()=>setMode(m=>m==="dark"?"light":"dark")} style={{padding:"5px 7px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:12}} title={mode==="dark"?"Switch to day mode":"Switch to night mode"}>{mode==="dark"?"☀️":"🌙"}</button>
        <button onClick={onSignOut} style={{padding:"5px 8px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:11}} title="Sign out">↩</button>
      </div>
    </header>

    <div style={{position:"relative",zIndex:1,maxWidth:isBottomNav?900:1120,margin:mode==="dark"?"0 auto":"16px auto",display:isBottomNav?"block":"flex",alignItems:"flex-start",gap:16,padding:isBottomNav?undefined:"0 14px"}}>
      {/* Desktop sidebar nav — on mobile/tablet widths (isBottomNav) the same tabs show
          in the bottom nav bar instead (see below), so this is skipped there. */}
      {!isBottomNav&&(
        <nav style={{width:190,flexShrink:0,position:"sticky",top:71,display:"flex",flexDirection:"column",gap:3,background:mode==="dark"?AS.surfaceColor:"rgba(255,255,255,0.93)",border:`1px solid ${AS.borderColor}`,borderRadius:14,padding:10,boxShadow:mode==="dark"?"none":"0 10px 40px rgba(20,83,45,0.1)"}}>
          {navTabs.map(([v,icon,label])=>(
            <button key={v} onClick={()=>navigateView(v)} style={{display:"flex",alignItems:"center",gap:9,padding:"9px 11px",borderRadius:8,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,textAlign:"left",background:view===v?ac:"transparent",color:view===v?"#fff":"var(--c-text2)"}}>
              <Icon name={icon} size={15}/>{label}
            </button>
          ))}
        </nav>
      )}
      <div style={{flex:1,minWidth:0,maxWidth:isBottomNav?900:undefined,margin:isBottomNav?"0 auto":undefined,padding:isMobile?"10px 10px calc(var(--nav-h,65px) + 16px)":isBottomNav?"16px 14px calc(var(--nav-h,65px) + 16px)":"16px",background:mode==="dark"?"transparent":"rgba(255,255,255,0.93)",borderRadius:mode==="dark"?0:18,boxShadow:mode==="dark"?"none":"0 10px 40px rgba(20,83,45,0.12)"}}>
      {/* Welcome message */}
      <div style={{...C,padding:16,marginBottom:isMobile?12:16,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        {viewer.player_id&&viewer.player_link_approved&&(
          <PlayerPhotoUploader playerId={viewer.player_id} photoUrl={linkedPlayer?.photo_url} name={linkedPlayer?.name} size={48} ac={ac} token={auth.token} canEdit={true} onUploaded={url=>setPlayers(prev=>prev.map(p=>p.id===viewer.player_id?{...p,photo_url:url}:p))}/>
        )}
        <div style={{flex:1}}>
          <div style={{fontSize:15,fontWeight:700,marginBottom:2}}>Welcome back, {(viewer.player_id?linkedPlayer?.name:viewer.name)?.split(" ")[0]||"there"} 👋</div>
          <div style={{fontSize:11,color:"var(--c-muted)"}}>{team?.name}</div>
        </div>
        {allSeasons.length>1&&(
          <select value={viewSeasonId||""} onChange={e=>switchSeason(e.target.value||null)} style={{padding:"6px 9px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text2)",fontSize:11,cursor:"pointer"}}>
            <option value="">Current Season</option>
            {allSeasons.filter(s=>!s.is_current).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
      </div>
      {viewSeasonId&&(
        <div style={{background:"#fef3c7",border:"1px solid #f59e0b55",borderRadius:9,padding:"9px 13px",marginBottom:isMobile?12:16,fontSize:11,color:"#92400e",fontWeight:600}}>
          📁 Viewing {allSeasons.find(s=>s.id===viewSeasonId)?.name||"a past season"}{!team&&" — you weren't on a team that season."}
        </div>
      )}

      {/* New achievements notification */}
      {featAchievements&&newAchievements.length>0&&(
        <div style={{background:"linear-gradient(135deg,#f59e0b22,#f59e0b11)",border:"1px solid #f59e0b55",borderRadius:11,padding:16,marginBottom:isMobile?12:16}}>
          <div style={{fontSize:14,fontWeight:800,marginBottom:8}}>🎉 Congratulations! You've earned the following achievement{newAchievements.length===1?"":"s"}:</div>
          <div style={{marginBottom:10}}><AchievementBadges rows={newAchievements} size="lg"/></div>
          <button onClick={dismissNewAchievements} style={{padding:"7px 16px",borderRadius:7,border:"none",background:"#f59e0b",color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>Got it! Dismiss</button>
        </div>
      )}

      {/* ── HOME ── */}
      {view==="home"&&(()=>{
        const blockDefs=[
          {id:"myTeam",node:(
            <div>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>My Team</div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:isMobile?7:9}}>
                {[{l:"Players",v:stats.total,i:"👥"},{l:"Team Goals",v:stats.goals,i:"🎯"},{l:"Matches Played",v:playedMatches.length,i:"⚽"},{l:"Top Scorer",v:stats.top?.name?.split(" ")[0]||"—",i:"⭐"}].map(c=>(
                  <div key={c.l} style={{...C,padding:13}}>
                    <div style={{fontSize:16,marginBottom:4}}>{c.i}</div>
                    <div style={{fontSize:18,fontWeight:700,color:"var(--c-textbright)"}}>{c.v}</div>
                    <div style={{fontSize:9,color:"var(--c-muted)",marginTop:1}}>{c.l}</div>
                  </div>
                ))}
              </div>
            </div>
          )},
          {id:"achievements",node:(
            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14}}>
              {/* Each column is a flex column with the card set to flex:1, so the two cards
                  still match heights whichever has more badges. The card used to be
                  height:100% instead, which resolved to the height of this wrapper —
                  heading included — so the card overflowed the wrapper by exactly the
                  heading's height and collided with whatever block came next. */}
              <div style={{display:"flex",flexDirection:"column",minWidth:0}}>
                <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>🏅 Team Achievements</div>
                <div style={{...C,padding:14,flex:1}}><AchievementBadges rows={teamAchievements} emptyText="Your team hasn't earned any achievements yet." size="lg"/></div>
              </div>
              <div style={{display:"flex",flexDirection:"column",minWidth:0}}>
                <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>🏅 My Achievements</div>
                <div style={{...C,padding:14,flex:1}}>
                  {viewer.player_id&&viewer.player_link_approved?(
                    <AchievementBadges rows={myAchievements} emptyText="No achievements yet — keep playing!" size="lg"/>
                  ):(
                    <div style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic"}}>Link your account below to see your own achievements here.</div>
                  )}
                </div>
              </div>
            </div>
          )},
          {id:"nextFixture",node:(
            <div>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Next Fixture</div>
              {nextFixture?(
                <div onClick={()=>openMatchDetail(nextFixture)} style={{...C,padding:14,cursor:"pointer"}}>
                  <div style={{fontWeight:700,fontSize:14,marginBottom:3}}>vs {nextFixture.opponent}</div>
                  <div style={{fontSize:11,color:"var(--c-muted)"}}>{fmtDate(nextFixture.date)}{nextFixture.meeting_time?` · 🕒 Meet ${nextFixture.meeting_time}`:""}{nextFixture.kickoff?` · ⏱ KO ${nextFixture.kickoff}`:""}{nextFixture.venue?` · ${nextFixture.venue}`:""}</div>
                </div>
              ):(
                <div style={{...C,padding:20,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No upcoming fixtures scheduled.</div>
              )}
            </div>
          )},
          {id:"recentFixtures",node:(
            <div>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Recent Fixtures</div>
              {recentFixtures.length===0?(
                <div style={{...C,padding:20,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No matches played yet.</div>
              ):(
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))",gap:9}}>
                  {recentFixtures.map(m=>{const r=rb(m);return(
                    <div key={m.id} onClick={()=>openMatchDetail(m)} style={{...C,padding:12,cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>vs {m.opponent}</div>
                        <div style={{fontSize:10,color:"var(--c-muted)",marginTop:2}}>{fmtDate(m.date)}</div>
                      </div>
                      <div style={{fontSize:16,fontWeight:900,color:r.color,flexShrink:0}}>{m.ourScore}–{m.oppScore}</div>
                    </div>
                  );})}
                </div>
              )}
            </div>
          )},
        ];
        if(viewer.player_id&&viewer.player_link_approved){
          blockDefs.push(
            {id:"myKeyStats",node:(
              <div>
                <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>My Key Stats</div>
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:9}}>
                    {[["My Goals",linkedPlayer?.goals||0,ac],["My Assists",linkedPlayer?.assists||0,"#8b5cf6"]].map(([l,v,c])=>(
                      <div key={l} style={{...C,padding:13,textAlign:"center"}}>
                        <div style={{fontSize:19,fontWeight:700,color:c}}>{v}</div>
                        <div style={{fontSize:10,color:"var(--c-muted)",marginTop:2}}>{l}</div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Attendance</div>
                    <div style={{...C,padding:14,display:"flex",flexDirection:"column",gap:14}}>
                      {[["Match",myAtt?.match,teamAtt.match],["Training",myAtt?.training,teamAtt.training],["Overall",myAtt?.overall,teamAtt.overall]].map(([label,mine,teamPct])=>(
                        <div key={label}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:5}}>
                            <span style={{fontSize:12,fontWeight:700}}>{label}</span>
                            <span style={{fontSize:11,color:"var(--c-muted)"}}>You: <b style={{color:mine===null?"var(--c-muted2)":mine>=80?"#22c55e":mine>=50?"#eab308":"#ef4444"}}>{mine!==null?`${mine}%`:"—"}</b> <span style={{color:"var(--c-muted2)"}}>· Team avg: {teamPct!==null?`${teamPct}%`:"—"}</span></span>
                          </div>
                          <div style={{position:"relative",height:7,background:AS.bgColor,borderRadius:4,overflow:"visible"}}>
                            <div style={{position:"absolute",left:0,top:0,bottom:0,width:`${mine??0}%`,background:mine===null?"var(--c-muted2)":mine>=80?"#22c55e":mine>=50?"#eab308":"#ef4444",borderRadius:4,transition:"width 0.3s"}}/>
                            {teamPct!==null&&<div title={`Team average: ${teamPct}%`} style={{position:"absolute",left:`${teamPct}%`,top:-2,width:2,height:11,background:"var(--c-text2)",transform:"translateX(-1px)"}}/>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {featPlayerRatings&&(
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Performance Ratings (avg)</div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:9}}>
                      <div style={{...C,padding:13,textAlign:"center"}}>
                        <div style={{fontSize:19,fontWeight:700,color:avgCoachRating!==null?ac:"var(--c-muted2)"}}>{avgCoachRating!==null?`${avgCoachRating}/10`:"—"}</div>
                        <div style={{fontSize:10,color:"var(--c-muted)",marginTop:2}}>Coach's Rating</div>
                      </div>
                      <div style={{...C,padding:13,textAlign:"center"}}>
                        <div style={{fontSize:19,fontWeight:700,color:avgSelfRating!==null?ac:"var(--c-muted2)"}}>{avgSelfRating!==null?`${avgSelfRating}/10`:"—"}</div>
                        <div style={{fontSize:10,color:"var(--c-muted)",marginTop:2}}>Your Rating</div>
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              </div>
            )},
            {id:"myDevelopment",node:(
              <div>
                <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>📈 My Development</div>
                <div style={{...C,padding:14}}><PlayerDevelopmentSection playerId={viewer.player_id} token={auth.token} canEdit={false} ac={ac} AS={AS}/></div>
              </div>
            )},
            {id:"coachNotes",node:(
              <div>
                <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>📝 Notes from your coach</div>
                <div style={{...C,padding:14}}>
                  <NotesThread token={auth.token} orgId={team.org_id} playerId={viewer.player_id} playerName={linkedPlayer?.name} senderType="player" senderId={viewer.id} ac={ac} AS={AS} isMobile={isMobile} onActivity={loadPlayerUnread}/>
                </div>
              </div>
            )},
          );
        }
        const visibleBlockDefs=blockDefs.filter(b=>(b.id!=="achievements"||featAchievements)&&(b.id!=="myDevelopment"||featPlayerDev));
        return(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {!viewer.player_id?(
            <div style={{...C,padding:18}}>
              <div style={{fontSize:14,fontWeight:700,marginBottom:4}}>👋 Which player are you?</div>
              <div style={{fontSize:12,color:"var(--c-text2)",marginBottom:14,lineHeight:1.5}}>Link your account to your name on the roster to see your own stats and upcoming fixtures here. Your coach will need to approve it.</div>
              {linkError&&<div style={{background:"#fee2e2",color:"#991b1b",padding:"9px 11px",borderRadius:7,fontSize:12,marginBottom:12}}>{linkError}</div>}
              {roster===null?(
                <div style={{fontSize:12,color:"var(--c-muted2)"}}>Loading roster…</div>
              ):roster.length===0?(
                <div style={{fontSize:12,color:"var(--c-muted2)"}}>No players on the roster yet — check back later.</div>
              ):(()=>{
                const ranked=[...roster].map(p=>({...p,sim:nameSimilarity(viewer.name,p.name)})).sort((a,b)=>b.sim-a.sim);
                const best=ranked[0];
                const confident=best&&best.sim>=0.45;
                if(linkStage==="suggest"&&confident){
                  return(
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:12,color:"var(--c-muted)",marginBottom:8}}>Based on your account name, is this you?</div>
                      <div style={{fontSize:17,fontWeight:700,marginBottom:14}}>{best.name}</div>
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={()=>requestLink(best.id)} disabled={linkSaving} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:13,cursor:"pointer"}}>{linkSaving?"Requesting…":"✓ Yes, that's me"}</button>
                        <button onClick={()=>setLinkStage("list")} style={{flex:1,padding:"10px",borderRadius:8,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-text2)",fontWeight:600,fontSize:13,cursor:"pointer"}}>No, not me</button>
                      </div>
                    </div>
                  );
                }
                const shortlist=ranked.filter(p=>!confident||p.id!==best.id).slice(0,4);
                return(<>
                  <div style={{fontSize:11,color:"var(--c-muted)",marginBottom:8}}>{confident?"Other similar names:":"Select your name:"}</div>
                  <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
                    {shortlist.map(p=>(
                      <button key={p.id} onClick={()=>requestLink(p.id)} disabled={linkSaving} style={{padding:"10px 12px",borderRadius:8,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontWeight:600,fontSize:13,cursor:"pointer",textAlign:"left"}}>{p.name}</button>
                    ))}
                  </div>
                  <details>
                    <summary style={{fontSize:11,color:"var(--c-muted)",cursor:"pointer",marginBottom:4}}>Not seeing your name? Browse full roster</summary>
                    <select value={linkPick} onChange={e=>setLinkPick(e.target.value)} style={{width:"100%",padding:"10px 11px",borderRadius:8,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:13,marginTop:8,marginBottom:10,cursor:"pointer"}}>
                      <option value="">— Select your name —</option>
                      {[...roster].sort((a,b)=>a.name.localeCompare(b.name)).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <button onClick={()=>requestLink()} disabled={!linkPick||linkSaving} style={{width:"100%",padding:"10px",borderRadius:8,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:13,cursor:linkPick?"pointer":"default",opacity:linkPick?1:0.5}}>{linkSaving?"Requesting…":"Request Link"}</button>
                  </details>
                </>);
              })()}
            </div>
          ):!viewer.player_link_approved?(
            <div style={{...C,padding:18,textAlign:"center"}}>
              <div style={{fontSize:30,marginBottom:8}}>⏳</div>
              <div style={{fontSize:14,fontWeight:700,marginBottom:4}}>Waiting for approval</div>
              <div style={{fontSize:12,color:"var(--c-text2)",marginBottom:14,lineHeight:1.5}}>You've asked to be linked to <b>{linkedPlayer?.name||"your selected player"}</b>. Your coach needs to approve this before your personal stats show up here.</div>
              {linkError&&<div style={{background:"#fee2e2",color:"#991b1b",padding:"9px 11px",borderRadius:7,fontSize:12,marginBottom:12,textAlign:"left"}}>{linkError}</div>}
              <button onClick={undoLinkRequest} style={{padding:"8px 16px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:12,cursor:"pointer"}}>Pick someone else</button>
            </div>
          ):null}
          <ReorderableBlocks blocks={visibleBlockDefs} order={homeOrder} onOrderChange={newOrder=>{setHomeOrder(newOrder);saveBlockOrder(auth.token,viewer.id,"player_home",newOrder);}}/>
        </div>
        );
      })()}

      {/* ── MATCHES ── */}
      {view==="matches"&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{display:"flex",justifyContent:"flex-end"}}>
            <div style={{display:"flex",gap:3,background:AS.bgColor,borderRadius:7,padding:3}}>
              {[["list","☰ List"],["calendar","📅 Calendar"]].map(([v,l])=>(
                <button key={v} onClick={()=>setMatchesViewMode(v)} style={{padding:"6px 11px",borderRadius:5,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,background:matchesViewMode===v?ac:"transparent",color:matchesViewMode===v?"#fff":"var(--c-muted)"}}>{l}</button>
              ))}
            </div>
          </div>
          {matchesViewMode==="calendar"&&(
            (upcomingMatches.length+playedMatches.length+tournaments.length)===0?(
              <div style={{...C,padding:24,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No fixtures yet.</div>
            ):(
              <div style={{...C,padding:14}}>
                <MatchCalendarView ac={ac} AS={AS} items={[
                  ...upcomingMatches.map(m=>({id:m.id,date:m.date,title:`vs ${m.opponent}`,subtitle:m.kickoff?`⏱ ${m.kickoff}`:(m.venue||"")})),
                  ...playedMatches.map(m=>{const r=rb(m);return{id:m.id,date:m.date,title:`vs ${m.opponent}`,resultLabel:`${m.ourScore}–${m.oppScore}`,resultColor:r.color,played:true};}),
                  ...tournaments.map(t=>({id:t.id,date:t.date,title:`🏆 ${t.name}`,subtitle:`${t.matches.length} matches`,isTournament:true})),
                ]} onSelectItem={it=>{
                  if(it.isTournament){setExpT(e=>({...e,[it.id]:true}));setMatchesViewMode("list");return;}
                  const m=[...upcomingMatches,...playedMatches,...tournaments.flatMap(t=>t.matches)].find(x=>x.id===it.id);
                  if(m)openMatchDetail(m);
                }}/>
              </div>
            )
          )}
          {matchesViewMode==="list"&&(<>
          {/* Upcoming */}
          <div>
            <h3 style={{margin:"0 0 8px",fontSize:11,fontWeight:700,color:"var(--c-text2)",textTransform:"uppercase",letterSpacing:"0.05em"}}>📅 Upcoming</h3>
            {upcomingMatches.length===0?(
              <div style={{...C,padding:20,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No upcoming matches scheduled.</div>
            ):(
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {upcomingMatches.map(m=>(
                  <div key={m.id} style={{...C,display:"flex",alignItems:"center",gap:10,padding:"11px 13px",cursor:"pointer"}} onClick={()=>openMatchDetail(m)}>
                    <div style={{width:34,height:34,borderRadius:8,background:ac+"22",border:`1px solid ${ac}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15}}>⚽</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:12}}>vs {m.opponent}</div>
                      <div style={{fontSize:10,color:"var(--c-muted)",marginTop:1}}>{fmtDate(m.date)}{m.kickoff?` · ⏱ ${m.kickoff}`:""}{m.venue?` · ${m.venue}`:""}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Results timeline */}
          <div>
            <h3 style={{margin:"0 0 8px",fontSize:11,fontWeight:700,color:"var(--c-text2)",textTransform:"uppercase",letterSpacing:"0.05em"}}>🏆 Results & Tournaments</h3>
            {playedMatches.length===0&&tournaments.length===0?(
              <div style={{...C,padding:20,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No results yet.</div>
            ):(
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {[...playedMatches,...tournaments].sort((a,b)=>(b.date||b.startDate||"").localeCompare(a.date||a.startDate||"")).map(item=>{
                  const isT=!!item.matches;
                  if(!isT){
                    const r=rb(item);
                    return(<div key={item.id} style={{...C,overflow:"hidden"}}>
                      <div style={{display:"flex",alignItems:"center",gap:10,padding:"11px 13px",cursor:"pointer"}} onClick={()=>openMatchDetail(item)}>
                        <div style={{width:34,height:34,borderRadius:8,background:r.bg,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:12,color:r.color,border:`1px solid ${r.color}44`}}>{r.label}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:700,fontSize:12}}>vs {item.opponent}</div>
                          <div style={{fontSize:10,color:"var(--c-muted)",marginTop:1}}>{fmtDate(item.date)}</div>
                        </div>
                        <div style={{fontSize:16,fontWeight:900,color:r.color}}>{item.ourScore}–{item.oppScore}</div>
                      </div>
                      {item.scorers?.length>0&&<div style={{padding:"0 13px 10px",display:"flex",gap:5,flexWrap:"wrap"}}>
                        {item.scorers.map(s=>{const p=players.find(pl=>pl.id===s.playerId);return p?(<span key={s.playerId} style={{fontSize:10,background:AS.bgColor,borderRadius:5,padding:"2px 7px",color:"var(--c-text2)"}}>⚽ {p.name.split(" ").pop()}{s.count>1?` ×${s.count}`:""}</span>):null;})}
                      </div>}
                    </div>);
                  }
                  const exp=expT[item.id];
                  const played=item.matches.filter(m=>m.played);
                  const w=played.filter(m=>m.ourScore>m.oppScore).length,d=played.filter(m=>m.ourScore===m.oppScore).length,l=played.filter(m=>m.ourScore<m.oppScore).length;
                  return(<div key={item.id} style={{...C,overflow:"hidden"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,padding:"11px 13px",cursor:"pointer"}} onClick={()=>setExpT(e=>({...e,[item.id]:!e[item.id]}))}>
                      <div style={{width:34,height:34,borderRadius:8,background:ac+"22",border:`1px solid ${ac}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15}}>🏆</div>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700,fontSize:12}}>{item.name}</div>
                        <div style={{fontSize:10,color:"var(--c-muted)",marginTop:1}}>{fmtDate(item.date)} · {item.matches.length} matches{played.length>0&&<span style={{marginLeft:6}}>W{w} D{d} L{l}</span>}</div>
                      </div>
                      <span style={{color:"var(--c-muted)",fontSize:11}}>{exp?"▲":"▼"}</span>
                    </div>
                    {exp&&<div style={{borderTop:`1px solid ${AS.borderColor}`,padding:"7px 13px",display:"flex",flexDirection:"column",gap:4}}>
                      {item.matches.map((m,idx)=>{const r=rb(m);return(
                        <div key={m.id} style={{display:"flex",alignItems:"center",gap:7,padding:"7px 9px",background:AS.bgColor,borderRadius:7,cursor:"pointer"}} onClick={()=>openMatchDetail(m)}>
                          <span style={{fontSize:9,fontWeight:700,color:"var(--c-muted)",minWidth:18}}>M{idx+1}</span>
                          <span style={{flex:1,fontWeight:600,fontSize:11}}>vs {m.opponent||"TBC"}</span>
                          {m.played?<span style={{fontSize:12,fontWeight:800,color:r.color}}>{m.ourScore}–{m.oppScore}</span>:<span style={{fontSize:9,color:"var(--c-muted2)"}}>Upcoming</span>}
                        </div>
                      );})}
                    </div>}
                  </div>);
                })}
              </div>
            )}
          </div>
          </>)}
        </div>
      )}

      {/* ── PLAYERS ── */}
      {view==="players"&&(()=>{
        // One column template shared by the header row and every player row — they have
        // to stay identical or the headings drift out of line with the numbers under them.
        const GRID=isMobile?"26px 1fr 38px 30px 30px 30px":"36px minmax(140px,260px) 70px 60px 60px 60px";
        return(
        <div style={{...C,overflow:"hidden"}}>
          <div style={{padding:"8px 12px",borderBottom:`1px solid ${AS.borderColor}`,fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Players ({players.length})</div>
          {players.length===0?(
            <div style={{padding:30,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No players added yet.</div>
          ):(<>
            {/* No date of birth and no age. Neither is fetched — the select above asks
                for six named columns and dob isn't one of them — so there is nothing
                here to leak even in the network tab. Age was briefly shown in whole
                years, but a whole-year age is still a disclosure and keeping it meant
                sending every teammate's exact DOB to every player's browser to compute
                it. Coaches keep both, in Player Base, where they're needed for
                age-group eligibility. */}
            <div style={{display:"grid",gridTemplateColumns:GRID,gap:isMobile?4:14,padding:"5px 11px",fontSize:8,fontWeight:600,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>
              <span>#</span><span>Player</span><span>Pos</span><span title="Matches played">MP</span><span title="Goals">G</span><span title="Assists">A</span>
            </div>
            {players.map(p=>(
              <div key={p.id} style={{display:"grid",gridTemplateColumns:GRID,gap:isMobile?4:14,alignItems:"center",padding:"9px 11px",borderTop:`1px solid ${AS.borderColor}`}}>
                <span style={{fontWeight:700,color:"var(--c-muted)",fontSize:isMobile?10:12}}>{p.number}</span>
                <span style={{fontWeight:600,fontSize:isMobile?11:13}}><Flag value={p.nationality}/> {p.name}</span>
                <span style={{background:AS.bgColor,borderRadius:5,padding:isMobile?"1px 4px":"3px 8px",fontSize:isMobile?8:11,fontWeight:700,color:"#93c5fd",textAlign:"center"}}>{p.position}</span>
                <span style={{fontWeight:700,fontSize:isMobile?11:13,color:"var(--c-text2)"}}>{matchesPlayedById[p.id]||0}</span>
                <span style={{fontWeight:700,fontSize:isMobile?11:13,color:ac}}>{p.goals||0}</span>
                <span style={{fontWeight:700,fontSize:isMobile?11:13,color:"#8b5cf6"}}>{p.assists||0}</span>
              </div>
            ))}
          </>)}
        </div>
        );
      })()}

      {/* ── STATS ── */}
      {view==="stats"&&(()=>{
        const isLinked=viewer.player_id&&viewer.player_link_approved;
        const statOptions=[{id:"matchResults",label:"Match Results"},{id:"homeAway",label:"Home vs Away"},isLinked&&{id:"ratingTimeline",label:"Your Rating Timeline"},isLinked&&{id:"goalsAssists",label:"Goals & Assists"},isLinked&&{id:"attendance",label:"Attendance"},isLinked&&featMatchDay&&{id:"matchTime",label:"Match Time"},{id:"topScorers",label:"Top Scorers"},{id:"topAssisters",label:"Top Assisters"}].filter(Boolean);
        const myMatchTimeRows=myMatchTime.map(r=>({fixture:fixtures.find(f=>f.id===r.fixture_id),secs:r.seconds_played})).filter(r=>r.fixture).sort((a,b)=>new Date(a.fixture.date)-new Date(b.fixture.date));
        const myMatchTimeTotal=myMatchTimeRows.reduce((s,r)=>s+r.secs,0);
        return(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{display:"flex",justifyContent:"flex-end"}}>
            <StatsFilterMenu options={statOptions} visible={statsVisible} setVisible={setStatsVisible} AS={AS}/>
          </div>
          {statsVisible.has("matchResults")&&(()=>{
            const wins=playedMatches.filter(f=>f.ourScore>f.oppScore).length;
            const draws=playedMatches.filter(f=>f.ourScore===f.oppScore).length;
            const losses=playedMatches.filter(f=>f.ourScore<f.oppScore).length;
            const cleanSheets=playedMatches.filter(f=>f.oppScore===0).length;
            return(
              <div style={{...C,padding:16}}>
                <h3 style={{margin:"0 0 12px",fontSize:11,fontWeight:600,color:"var(--c-text2)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Match Results</h3>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:9}}>
                  {[["Wins",wins,"#22c55e"],["Draws",draws,"#f59e0b"],["Losses",losses,"#ef4444"],["Clean Sheets",cleanSheets,ac]].map(([l,v,c])=>(
                    <div key={l} style={{background:AS.bgColor,borderRadius:8,padding:"11px 6px",textAlign:"center"}}>
                      <div style={{fontSize:18,fontWeight:700,color:c}}>{v}</div>
                      <div style={{fontSize:9,color:"var(--c-muted)",marginTop:2}}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          {statsVisible.has("homeAway")&&<HomeAwaySplitCard matches={playedMatches} AS={AS}/>}
          {isLinked&&(<>
            {statsVisible.has("ratingTimeline")&&<div style={{...C,padding:16}}>
              <h3 style={{margin:"0 0 12px",fontSize:11,fontWeight:600,color:"var(--c-text2)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Your Rating Timeline</h3>
              <RatingTimeline points={[...playedMatches].sort((a,b)=>new Date(a.date)-new Date(b.date)).filter(m=>myRatings.some(r=>r.fixture_id===m.id)).map(m=>({date:m.date,opponent:m.opponent,player:myRatings.find(r=>r.fixture_id===m.id&&r.source==="player")?.rating??null,coach:myRatings.find(r=>r.fixture_id===m.id&&r.source==="coach")?.rating??null}))} ac={ac}/>
            </div>}
            {statsVisible.has("goalsAssists")&&<div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:9}}>
              {[["Goals",linkedPlayer?.goals||0,ac],["Assists",linkedPlayer?.assists||0,"#8b5cf6"]].map(([l,v,c])=>(
                <div key={l} style={{...C,padding:13,textAlign:"center"}}>
                  <div style={{fontSize:19,fontWeight:700,color:c}}>{v}</div>
                  <div style={{fontSize:10,color:"var(--c-muted)",marginTop:2}}>{l}</div>
                </div>
              ))}
            </div>}
            {statsVisible.has("attendance")&&<div>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Attendance</div>
              <div style={{...C,padding:14,display:"flex",flexDirection:"column",gap:14}}>
                {[["Match",myAtt?.match,teamAtt.match],["Training",myAtt?.training,teamAtt.training],["Overall",myAtt?.overall,teamAtt.overall]].map(([label,mine,teamPct])=>(
                  <div key={label}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:5}}>
                      <span style={{fontSize:12,fontWeight:700}}>{label}</span>
                      <span style={{fontSize:11,color:"var(--c-muted)"}}>You: <b style={{color:mine===null?"var(--c-muted2)":mine>=80?"#22c55e":mine>=50?"#eab308":"#ef4444"}}>{mine!==null?`${mine}%`:"—"}</b> <span style={{color:"var(--c-muted2)"}}>· Team avg: {teamPct!==null?`${teamPct}%`:"—"}</span></span>
                    </div>
                    <div style={{position:"relative",height:7,background:AS.bgColor,borderRadius:4,overflow:"visible"}}>
                      <div style={{position:"absolute",left:0,top:0,bottom:0,width:`${mine??0}%`,background:mine===null?"var(--c-muted2)":mine>=80?"#22c55e":mine>=50?"#eab308":"#ef4444",borderRadius:4,transition:"width 0.3s"}}/>
                      {teamPct!==null&&<div title={`Team average: ${teamPct}%`} style={{position:"absolute",left:`${teamPct}%`,top:-2,width:2,height:11,background:"var(--c-text2)",transform:"translateX(-1px)"}}/>}
                    </div>
                  </div>
                ))}
              </div>
            </div>}
            {featMatchDay&&statsVisible.has("matchTime")&&<div>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>🏟️ Match Time</div>
              <div style={{...C,padding:14}}>
                {myMatchTimeRows.length===0?<div style={{color:"var(--c-muted2)",fontSize:11}}>No Match Day data yet.</div>:(<>
                  <div style={{display:"flex",flexDirection:"column",gap:7,marginBottom:12}}>
                    {myMatchTimeRows.map(r=>(
                      <div key={r.fixture.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontSize:12,color:"var(--c-text2)"}}>vs {r.fixture.opponent||"TBC"} <span style={{color:"var(--c-muted2)",fontSize:10}}>· {fmtDateShort(r.fixture.date)}</span></span>
                        <span style={{fontSize:12,fontWeight:700,color:ac}}>{fmtMins(r.secs)}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:10,borderTop:`1px solid ${AS.borderColor}`}}>
                    <span style={{fontSize:12,fontWeight:700}}>Total</span>
                    <span style={{fontSize:14,fontWeight:800,color:ac}}>{fmtMins(myMatchTimeTotal)}</span>
                  </div>
                </>)}
              </div>
            </div>}
          </>)}
          {players.length===0?(
            <div style={{...C,padding:30,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No players yet.</div>
          ):(<>
          {[["Top Scorers","topScorers",[...players].sort((a,b)=>(b.goals||0)-(a.goals||0)).slice(0,8),"goals",stats.goals,ac],["Top Assisters","topAssisters",[...players].sort((a,b)=>(b.assists||0)-(a.assists||0)).slice(0,8),"assists",stats.assists,"#8b5cf6"]].filter(([,id])=>statsVisible.has(id)).map(([title,id,list,key,total,col])=>(
            <div key={title} style={{...C,padding:16}}>
              <h3 style={{margin:"0 0 12px",fontSize:11,fontWeight:600,color:"var(--c-text2)",textTransform:"uppercase",letterSpacing:"0.05em"}}>{title}</h3>
              {list.map((p,i)=>(
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:9,marginBottom:8}}>
                  <span style={{width:16,color:i===0?"#f59e0b":"var(--c-muted2)",fontWeight:700,fontSize:12}}>{i+1}</span>
                  <span style={{flex:1,fontWeight:600,fontSize:12}}><Flag value={p.nationality}/> {p.name}</span>
                  <span style={{fontSize:10,color:"var(--c-muted)",marginRight:4}}>{p.position}</span>
                  <div style={{width:65,background:AS.bgColor,borderRadius:3,height:4,overflow:"hidden"}}><div style={{width:`${total?((p[key]||0)/total)*100:0}%`,background:col,height:"100%",borderRadius:3}}/></div>
                  <span style={{fontWeight:700,width:18,textAlign:"right",fontSize:12}}>{p[key]||0}</span>
                </div>
              ))}
            </div>
          ))}
          </>)}
        </div>
        );
      })()}

      {/* ── CHAT ── */}
      {view==="chat"&&(
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div style={{display:"flex",gap:3,background:AS.bgColor,borderRadius:8,padding:3,border:`1px solid ${AS.borderColor}`,maxWidth:300}}>
            {[["team","👥 Team Chat"],["coach","🧑‍🏫 Coach Chat"]].map(([v,l])=>(
              <button key={v} onClick={()=>setChatMode(v)} style={{flex:1,padding:"8px",borderRadius:6,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,background:chatMode===v?ac:"transparent",color:chatMode===v?"#fff":"var(--c-muted)"}}>{l}</button>
            ))}
          </div>
          <div style={{fontSize:11,color:"var(--c-muted)",lineHeight:1.5}}>{chatMode==="team"?"Chat with your whole team and coaches together.":"A private conversation between just you and your coach."}</div>
          {chatMode==="team"?(
            <div style={{...C,padding:16,height:isMobile?"calc(100vh - 280px)":540,display:"flex",flexDirection:"column"}}>
              <TeamChatView teamId={team?.id} channel="player" senderType="player" senderId={auth.viewer.id} senderName={viewer.name} token={auth.token} ac={ac} AS={AS} canPost={(team?.player_chat_mode||"twoway")==="twoway"}/>
            </div>
          ):(
            viewer.player_id?(
              <div style={{...C,padding:16}}>
                <NotesThread token={auth.token} orgId={team.org_id} playerId={viewer.player_id} playerName={linkedPlayer?.name} senderType="player" senderId={viewer.id} ac={ac} AS={AS} isMobile={isMobile} onActivity={loadPlayerUnread}/>
              </div>
            ):(
              <div style={{...C,padding:30,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>Link your account to your name on the roster (from Home) to message your coach directly.</div>
            )
          )}
        </div>
      )}

      {/* ── LEAGUES ── */}
      {view==="leagues"&&featLeagues&&(
        <PlayerLeaguesView teamId={team?.id} token={auth.token} ac={ac} AS={AS}/>
      )}

      {/* ── PAYMENTS ── */}
      {view==="payments"&&featPayments&&paymentsVisible&&(
        viewer.player_id&&viewer.player_link_approved?(
          <MyPaymentsView teamId={team?.id} playerId={viewer.player_id} token={auth.token} ac={ac} AS={AS} org={orgBilling} accountRole="player"/>
        ):(
          <div style={{...C,padding:30,textAlign:"center",color:"var(--c-muted2)"}}>Link your account to a player on the roster to see your payment status here.</div>
        )
      )}

      {/* ── NOTIFICATIONS ── */}
      {view==="notifications"&&(
        <NotificationSettingsPanel token={auth.token} userId={auth.viewer.id} ac={ac} AS={AS}/>
      )}

      {/* ── ACCESSIBILITY ── */}
      {view==="accessibility"&&(
        <AccessibilitySettingsPanel ac={ac} AS={AS}/>
      )}
      </div>
    </div>

    {/* Mobile bottom nav — also used on tablet widths (e.g. iPad), where the top nav
        row gets too cramped to fit all the tabs. */}
    {isBottomNav&&<nav style={{position:"fixed",bottom:0,left:0,right:0,background:AS.surfaceColor,borderTop:`1px solid ${AS.borderColor}`,display:"flex",zIndex:200,paddingBottom:"env(safe-area-inset-bottom,0px)"}}>
      {navTabs.map(([v,icon,label])=>(
        <button key={v} onClick={()=>navigateView(v)} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2,padding:"7px 0",background:"none",border:"none",cursor:"pointer",color:view===v?ac:"var(--c-muted)",borderTop:view===v?`2px solid ${ac}`:"2px solid transparent"}}>
          <Icon name={icon} size={18}/>
          <span style={{fontSize:9,fontWeight:view===v?700:400}}>{label}</span>
        </button>
      ))}
    </nav>}

    {/* Back button reached the very first page with nowhere left to go — confirm before
        ever signing anyone out, rather than doing it silently. */}
    {showLogoutConfirm&&(
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:14}}>
        <div style={{background:AS.surfaceColor,borderRadius:13,border:"1px solid #7f1d1d55",width:"100%",maxWidth:340,padding:18}}>
          <div style={{fontSize:14,fontWeight:800,marginBottom:6}}>Log out?</div>
          <div style={{fontSize:12,color:"var(--c-muted)",lineHeight:1.5,marginBottom:16}}>You've gone back as far as this app can go. Do you want to log out?</div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>{setShowLogoutConfirm(false);onSignOut();}} style={{flex:1,padding:"9px",borderRadius:7,border:"none",background:"#ef4444",color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>Yes, Log Out</button>
            <button onClick={()=>setShowLogoutConfirm(false)} style={{padding:"9px 14px",borderRadius:7,border:"1px solid var(--c-border)",background:"none",color:"var(--c-text2)",fontSize:12,cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      </div>
    )}

    {/* Match detail (read-only) */}
    {detailMatch&&(()=>{
      const hasLineup=(detailMatch.slots||[]).some(s=>s.playerId);
      const hasNotes=!!detailMatch.notes||matchPlayerNotes.length>0;
      const availableTabs=[["result","📊 Result"],hasLineup&&["lineup","🗂 Lineup"],hasNotes&&["notes","📋 Notes"]].filter(Boolean);
      return(
      <div onClick={()=>setDetailMatch(null)} style={{position:"fixed",inset:0,bottom:isMobile?"var(--nav-h,65px)":0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:isMobile?"flex-end":"center",justifyContent:"center",zIndex:100,padding:isMobile?0:14}}>
        <div onClick={e=>e.stopPropagation()} style={{background:AS.surfaceColor,borderRadius:isMobile?"16px 16px 0 0":13,width:"100%",maxWidth:isMobile?"100%":760,border:`1px solid ${AS.borderColor}`,maxHeight:"85vh",overflowY:"auto",padding:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
            <div>
              <div style={{fontSize:17,fontWeight:800}}>vs {detailMatch.opponent||"TBC"}</div>
              {viewer.player_id&&viewer.player_link_approved&&(
                <button onClick={toggleMyReview} style={{marginTop:8,display:"flex",alignItems:"center",gap:5,padding:"5px 11px",borderRadius:7,border:myReview?"none":`1px solid ${AS.borderColor}`,background:myReview?"#22c55e":"none",color:myReview?"#fff":"var(--c-muted)",fontWeight:700,fontSize:11,cursor:"pointer"}}>{myReview?"✓ Reviewed":"Mark as Reviewed"}</button>
              )}
            </div>
            <button onClick={()=>setDetailMatch(null)} style={{background:"none",border:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:18}}>×</button>
          </div>
          {/* Meeting time / kick-off / date / location, big and clearly labelled rather
              than crammed into one small grey caption line. */}
          <div style={{marginBottom:14}}><MatchKeyInfo match={detailMatch} ac={ac} compact={isMobile}/></div>
          {availableTabs.length>1&&<div style={{display:"flex",gap:4,marginBottom:14,background:AS.bgColor,borderRadius:8,padding:4}}>
            {availableTabs.map(([v,l])=>(
              <button key={v} onClick={()=>setMatchTab(v)} style={{flex:1,padding:"7px",borderRadius:6,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,background:matchTab===v?ac:"transparent",color:matchTab===v?"#fff":"var(--c-muted)"}}>{l}</button>
            ))}
          </div>}
          {matchTab==="result"&&(detailMatch.played?(<>
            <div style={{background:AS.bgColor,borderRadius:10,padding:14,textAlign:"center",marginBottom:14}}>
              <div style={{fontSize:30,fontWeight:900,color:rb(detailMatch).color}}>{detailMatch.ourScore}–{detailMatch.oppScore}</div>
              <div style={{fontSize:11,fontWeight:700,color:rb(detailMatch).color,marginTop:4}}>{detailMatch.ourScore>detailMatch.oppScore?"WIN":detailMatch.ourScore<detailMatch.oppScore?"LOSS":"DRAW"}</div>
            </div>
            {detailMatch.scorers?.length>0&&<div style={{marginBottom:12}}>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:7}}>⚽ Goal Scorers</div>
              {detailMatch.scorers.map(s=>{const p=players.find(pl=>pl.id===s.playerId);return p?(<div key={s.playerId} style={{fontSize:12,padding:"5px 0"}}><Flag value={p.nationality}/> {p.name} {s.count>1?`(×${s.count})`:""}</div>):null;})}
            </div>}
            {detailMatch.assisters?.length>0&&<div>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:7}}>🎯 Assists</div>
              {detailMatch.assisters.map(s=>{const p=players.find(pl=>pl.id===s.playerId);return p?(<div key={s.playerId} style={{fontSize:12,padding:"5px 0"}}><Flag value={p.nationality}/> {p.name} {s.count>1?`(×${s.count})`:""}</div>):null;})}
            </div>}
            {featPlayerRatings&&viewer.player_id&&viewer.player_link_approved&&(()=>{
              const myPlayerRating=myRatings.find(r=>r.fixture_id===detailMatch.id&&r.source==="player");
              const myCoachRating=myRatings.find(r=>r.fixture_id===detailMatch.id&&r.source==="coach");
              return(
                <div style={{marginTop:14,paddingTop:14,borderTop:`1px solid ${AS.borderColor}`}}>
                  <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>⭐ Your Performance</div>
                  <div style={{display:"flex",gap:9,marginBottom:10}}>
                    <div style={{...C,flex:1,padding:10,textAlign:"center"}}>
                      <div style={{fontSize:19,fontWeight:700,color:myCoachRating?ac:"var(--c-muted2)"}}>{myCoachRating?`${myCoachRating.rating}/10`:"—"}</div>
                      <div style={{fontSize:9,color:"var(--c-muted)",marginTop:2}}>Coach's Rating</div>
                    </div>
                    <div style={{...C,flex:1,padding:10,textAlign:"center"}}>
                      <div style={{fontSize:19,fontWeight:700,color:myPlayerRating?ac:"var(--c-muted2)"}}>{myPlayerRating?`${myPlayerRating.rating}/10`:"—"}</div>
                      <div style={{fontSize:9,color:"var(--c-muted)",marginTop:2}}>Your Rating</div>
                    </div>
                  </div>
                  <div style={{fontSize:10,color:"var(--c-muted)",marginBottom:6}}>{myPlayerRating?"Update your rating:":"Rate your own performance:"}</div>
                  <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                    {[1,2,3,4,5,6,7,8,9,10].map(n=>(
                      <button key={n} onClick={()=>setMySelfRating(detailMatch.id,n)} style={{width:26,height:26,borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,background:myPlayerRating?.rating===n?ac:AS.bgColor,color:myPlayerRating?.rating===n?"#fff":"var(--c-muted)"}}>{n}</button>
                    ))}
                  </div>
                </div>
              );
            })()}
            {viewer.player_id&&viewer.player_link_approved&&(
              <div style={{marginTop:14,paddingTop:14,borderTop:`1px solid ${AS.borderColor}`}}>
                <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>📈 Rate Your Strengths & Development</div>
                <div style={{fontSize:10,color:"var(--c-muted2)",marginBottom:8}}>How did each one go this match? Adjust by up to ±1 — your coach can do the same, and the running total combines both.</div>
                <DevPointsPanel playerId={viewer.player_id} fixtureId={detailMatch.id} source="player" token={auth.token} ac={ac} AS={AS}/>
              </div>
            )}
          </>):(
            <div style={{textAlign:"center",color:"var(--c-muted)",fontSize:13,padding:"20px 0"}}>This match hasn't been played yet.</div>
          ))}
          {matchTab==="lineup"&&hasLineup&&(
            <ReadOnlyPitch players={players} slots={detailMatch.slots} subs={detailMatch.subs} tc={ac} strategies={teamStrategies.filter(s=>(detailMatch.attached_strategy_ids||[]).includes(s.id))} roles={[["👑 Captain",detailMatch.captain_id],["🚩 Corner Taker (Left)",detailMatch.corner_taker_left_id],["🚩 Corner Taker (Right)",detailMatch.corner_taker_right_id],["🎯 Free Kick Taker",detailMatch.free_kick_taker_id],["⚽ Penalty Taker",detailMatch.penalty_taker_id]].filter(([,pid])=>pid).map(([label,pid])=>[label,players.find(pl=>pl.id===pid)?.name||"—"])} isMobile={isMobile}/>
          )}
          {matchTab==="notes"&&hasNotes&&(
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {detailMatch.notes&&<div>
                <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:7}}>Match Notes</div>
                <div style={{...C,padding:12,fontSize:13,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{detailMatch.notes}</div>
              </div>}
              {matchPlayerNotes.length>0&&<div>
                <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:7}}>Individual Notes</div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {matchPlayerNotes.map(n=>{const p=players.find(pl=>pl.id===n.player_id);return p?(
                    <div key={n.id} style={{...C,padding:10}}>
                      <div style={{fontSize:12,fontWeight:700,marginBottom:3}}><Flag value={p.nationality}/> {p.name}</div>
                      <div style={{fontSize:12,color:"var(--c-text2)",whiteSpace:"pre-wrap"}}>{n.note}</div>
                    </div>
                  ):null;})}
                </div>
              </div>}
            </div>
          )}
        </div>
      </div>
      );
    })()}
  </div>);
}

// ── AdminApp ──────────────────────────────────────────────────────────────────
// Platform-level dashboard for internal admin accounts (see `platform_admins`
// table — not a per-org role). Lists every registered organisation with
// team/player/coach counts; clicking a row opens a detail panel with the head
// coach's info and how long the org has been on its current plan tier.

